import { Fill, Stroke, Style } from 'ol/style';
import { getVuetifyThemeVar } from '@/utils/mapLayerTheme';

function getThemeVar(variableName, fallback) {
  return getVuetifyThemeVar(variableName, fallback);
}

function withOpacity(color, opacity) {
  if (!color) return color;
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const fullHex = hex.length === 3 ? hex.split('').map((ch) => ch + ch).join('') : hex;
    const r = parseInt(fullHex.slice(0, 2), 16);
    const g = parseInt(fullHex.slice(2, 4), 16);
    const b = parseInt(fullHex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  if (color.startsWith('rgb(')) {
    return color.replace('rgb(', 'rgba(').replace(')', `, ${opacity})`);
  }
  return color;
}

/**
 * 폴리곤 스타일 관리 관련 기능을 담당하는 클래스
 */
export class StyleManager {
  /**
   * 기본 폴리곤 스타일 생성
   * @returns {Style} 기본 폴리곤 스타일
   */
  createDefaultPolygonStyle() {
    const surface = getThemeVar('--v-surface-base', '#FFFFFF');
    const info = getThemeVar('--v-info-base', '#1565C0');
    return new Style({
      fill: new Fill({
        color: withOpacity(surface, 0.12)
      }),
      stroke: new Stroke({
        color: info,
        width: 2
      })
    });
  }

  /**
   * 선택된 폴리곤 스타일 생성
   * @returns {Style} 선택된 폴리곤 스타일
   */
  createSelectedPolygonStyle() {
    const warning = getThemeVar('--v-warning-base', '#E65100');
    return new Style({
      fill: new Fill({
        color: withOpacity(warning, 0.3)
      }),
      stroke: new Stroke({
        color: warning,
        width: 3
      })
    });
  }

  /**
   * 호버된 폴리곤 스타일 생성
   * @returns {Style} 호버된 폴리곤 스타일
   */
  createHoveredPolygonStyle() {
    const success = getThemeVar('--v-success-base', '#2E7D32');
    return new Style({
      fill: new Fill({
        color: withOpacity(success, 0.2)
      }),
      stroke: new Stroke({
        color: success,
        width: 2.5
      })
    });
  }

  /**
   * 수정 중인 폴리곤 스타일 생성
   * @returns {Style} 수정 중인 폴리곤 스타일
   */
  createModifyingPolygonStyle() {
    const error = getThemeVar('--v-error-base', '#C62828');
    return new Style({
      fill: new Fill({
        color: withOpacity(error, 0.2)
      }),
      stroke: new Stroke({
        color: error,
        width: 3
      })
    });
  }

  /**
   * 다중 선택 스타일 생성
   * @returns {Style} 다중 선택 스타일
   */
  createSelectionStyle() {
    const warning = getThemeVar('--v-warning-base', '#E65100');
    return new Style({
      fill: new Fill({
        color: withOpacity(warning, 0.2)
      }),
      stroke: new Stroke({
        color: warning,
        width: 2,
        lineDash: [5, 5]
      })
    });
  }

  /**
   * 동적 스타일 함수 생성 (상태에 따라 다른 스타일 적용)
   * @param {Object} options - 스타일 옵션
   * @returns {Function} 동적 스타일 함수
   */
  createDynamicStyleFunction(options = {}) {
    const {
      defaultStyle = this.createDefaultPolygonStyle(),
      selectedStyle = this.createSelectedPolygonStyle(),
      hoveredStyle = this.createHoveredPolygonStyle(),
      modifyingStyle = this.createModifyingPolygonStyle()
    } = options;

    return (feature) => {
      const properties = feature.getProperties();

      if (properties.isModifying) {
        return modifyingStyle;
      }

      if (properties.isSelected) {
        return selectedStyle;
      }

      if (properties.isHovered) {
        return hoveredStyle;
      }

      return defaultStyle;
    };
  }

  /**
   * 폴리곤 피처에 상태 스타일 적용
   * @param {ol/Feature} feature - 폴리곤 피처
   * @param {string} state - 상태 ('default', 'selected', 'hovered', 'modifying')
   */
  applyStateToFeature(feature, state) {
    if (!feature) return;

    const properties = feature.getProperties();

    properties.isSelected = false;
    properties.isHovered = false;
    properties.isModifying = false;

    switch (state) {
      case 'selected':
        properties.isSelected = true;
        break;
      case 'hovered':
        properties.isHovered = true;
        break;
      case 'modifying':
        properties.isModifying = true;
        break;
      default:
        break;
    }

    feature.changed();
  }

  /**
   * 모든 폴리곤 피처를 기본 상태로 초기화
   * @param {ol/source/Vector} source - 벡터 소스
   */
  resetAllFeaturesToDefault(source) {
    if (!source) return;

    source.getFeatures().forEach(feature => {
      this.applyStateToFeature(feature, 'default');
    });
  }
}
